/**
 * Loại giao diện hiển thị cho người dùng — chọn trên trang admin (migration v100).
 *
 * 🔴 **Trước đây đây là biến BUILD-TIME `EVENT_UI`**, nên bật/tắt giao diện sự kiện
 * phải build + deploy lại cả frontend. Nay admin đổi ở `/admin/ui-config` và FE đọc
 * qua `GET /v1/ui-config`. Biến môi trường CHỈ CÒN LÀ ĐƯỜNG LÙI cho lần tải đầu
 * tiên khi chưa có câu trả lời nào từ máy chủ (và cho môi trường chưa chạy v100).
 *
 * ⚠️ **`isEventUIEnabled()` vẫn ĐỒNG BỘ** — nó bị gọi ngay trong thân render của
 * `top-nav` và `home-super-app`. Giá trị sống trong một store nhỏ đọc bằng
 * `useSyncExternalStore`, được **seed từ localStorage** ngay lúc module nạp: không
 * có bước seed ấy thì mọi lượt vào web đều vẽ giao diện thường trước rồi mới nhảy
 * sang giao diện sự kiện khi request về — một cú nháy thấy rõ ở trang chủ.
 *
 * ⚠️ **Đừng đổi `isEventUIEnabled()` thành async.** Component gọi nó trong render;
 * biến nó thành promise là mọi chỗ dùng phải thành state, và mỗi chỗ lại tự sinh
 * một nhịp nháy riêng.
 */

import { BOIDE_API_URL } from "@/lib/boide-api";
import { isUiMode, type UiMode } from "@/lib/events/registry";

export type { UiMode };

/**
 * Giá trị `ui_mode` của bản backend TRƯỚC v101, khi mới có đúng một sự kiện.
 *
 * 🔴 Phải giữ lại: FE và BE deploy rời nhau, nên luôn có một khoảng thời gian FE bản
 * mới nói chuyện với BE bản cũ (và một cache localStorage còn giữ chuỗi này từ lượt
 * truy cập trước). Bỏ nhánh quy đổi là trong khoảng ấy giao diện sự kiện tắt ngóm mà
 * không lỗi nào hiện ra.
 */
const LEGACY_EVENT = "event";

function normalizeMode(value: unknown): UiMode | null {
  if (value === LEGACY_EVENT) return "quoc_khanh";
  return isUiMode(value) ? value : null;
}

/** Khoá localStorage giữ câu trả lời gần nhất của máy chủ — chống nháy giao diện. */
const CACHE_KEY = "boide_ui_mode";

/** Giá trị từ biến môi trường — đường lùi khi chưa có câu trả lời của máy chủ. */
function envUiMode(): UiMode {
  try {
    const raw =
      (typeof import.meta !== "undefined" && import.meta.env
        ? (import.meta.env.EVENT_UI ?? import.meta.env.VITE_EVENT_UI)
        : undefined) ??
      (typeof process !== "undefined" && process.env
        ? (process.env.EVENT_UI ?? process.env.VITE_EVENT_UI)
        : undefined);

    if (raw === undefined || raw === null || raw === "") return "normal";
    if (typeof raw === "boolean") return raw ? "quoc_khanh" : "normal";
    if (typeof raw === "string") {
      const normalized = raw.trim().toLowerCase();
      // Dạng SLUG: `EVENT_UI=trung_thu`. Thêm sau, vì dạng boolean cũ chỉ diễn đạt
      // được đúng một lễ (2/9) — không có cách nào xem trước sáu lễ còn lại.
      if (isUiMode(normalized)) return normalized;
      const on =
        normalized === "true" || normalized === "1" || normalized === "yes" || normalized === "on";
      return on ? "quoc_khanh" : "normal";
    }
  } catch {
    /* Mặc định giao diện thường nếu có lỗi. */
  }
  return "normal";
}

/**
 * Chế độ ĐÈ từ biến môi trường — thắng cả cache lẫn câu trả lời của máy chủ.
 *
 * Dùng để XEM TRƯỚC một lễ ở máy mình mà không phải bật nó trên trang admin (đổi
 * `ui_config` là đổi cho MỌI người dùng thật).
 *
 * 🔴 CHỈ dạng SLUG mới đè (`EVENT_UI=trung_thu`). Dạng boolean cũ (`EVENT_UI=true`,
 * đang nằm trong `.env` của repo) vẫn chỉ là giá trị lần tải đầu — biến nó thành đè
 * là công tắc trên trang admin hết tác dụng ở mọi môi trường đã đặt biến ấy, mà dấu
 * hiệu duy nhất là giao diện không đổi khi admin bấm.
 */
function envForcedMode(): UiMode | null {
  try {
    const raw =
      (typeof import.meta !== "undefined" && import.meta.env
        ? (import.meta.env.EVENT_UI ?? import.meta.env.VITE_EVENT_UI)
        : undefined) ??
      (typeof process !== "undefined" && process.env
        ? (process.env.EVENT_UI ?? process.env.VITE_EVENT_UI)
        : undefined);
    if (typeof raw !== "string") return null;
    const normalized = raw.trim().toLowerCase();
    return isUiMode(normalized) ? normalized : null;
  } catch {
    return null;
  }
}

function readCache(): UiMode | null {
  try {
    return normalizeMode(localStorage.getItem(CACHE_KEY));
  } catch {
    // Private mode / site data bị chặn — vẫn chạy được, chỉ là lần đầu có thể nháy.
    return null;
  }
}

function writeCache(mode: UiMode) {
  try {
    localStorage.setItem(CACHE_KEY, mode);
  } catch {
    /* Không lưu được thì thôi — giá trị trong phiên hiện tại vẫn đúng. */
  }
}

/**
 * Dán `data-event="<slug>"` lên thẻ <html> — đây là thứ mở khoá toàn bộ token màu
 * `--event-*` trong `styles.css`; ở giao diện thường thì gỡ hẳn thuộc tính đi.
 *
 * 🔴 **Do STORE làm, không phải một `useEffect` ở `__root`.** `useEffect` chạy SAU
 * khi trình duyệt đã vẽ, nên sẽ có đúng một khung hình mà `--event-gold` chưa tồn
 * tại: `color-mix()` với biến rỗng thành khai báo không hợp lệ, và viền thẻ lời chúc
 * loé lên màu chữ trước khi về đúng vàng. Đặt ngay lúc module nạp (đồng bộ, trước cả
 * lần render đầu) thì không có khung hình nào sai.
 */
function syncDocumentEvent(mode: UiMode) {
  if (typeof document === "undefined") return; // SSR
  const root = document.documentElement;
  if (mode === "normal") root.removeAttribute("data-event");
  else root.setAttribute("data-event", mode);
}

// ── Store nhỏ, đọc bằng useSyncExternalStore ─────────────────────────────────
// Seed ngay lúc module nạp: cache của máy chủ trước, rồi mới tới biến môi trường.
// ⚠️ `envForcedMode()` phải đứng TRƯỚC `readCache()`: `localStorage` của máy đang xem
// thường còn giữ lễ mà máy chủ trả lần trước, và cache thắng thì biến đè vô nghĩa.
let currentMode: UiMode = envForcedMode() ?? readCache() ?? envUiMode();
syncDocumentEvent(currentMode);

const listeners = new Set<() => void>();

function setMode(next: UiMode) {
  if (next === currentMode) return;
  currentMode = next;
  syncDocumentEvent(next);
  for (const listener of listeners) listener();
}

export function subscribeUiMode(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getUiMode(): UiMode {
  return currentMode;
}

/**
 * Đúng khi đang bật MỘT giao diện sự kiện bất kỳ. Đồng bộ — gọi được thẳng trong render.
 *
 * ⚠️ Xét `!== "normal"` chứ không liệt kê từng slug: thêm một lễ mới thì hàm này
 * không phải sửa, và không có chỗ nào để quên.
 */
export function isEventUIEnabled(): boolean {
  return currentMode !== "normal";
}

let inFlight: Promise<UiMode> | null = null;

/**
 * Hỏi máy chủ loại giao diện đang bật rồi cập nhật store + localStorage.
 *
 * ⚠️ **Nuốt mọi lỗi.** Máy chủ lỗi, mất mạng, chưa chạy migration — không cái nào
 * được phép làm hỏng trang: giữ nguyên giá trị đang có (cache hoặc env).
 *
 * Gọi nhiều lần cùng lúc thì dùng chung một request (`inFlight`).
 */
export function loadUiMode(): Promise<UiMode> {
  // Đang bị env đè thì KHÔNG hỏi máy chủ: hỏi rồi bỏ câu trả lời chỉ tốn một lượt
  // mạng ở mọi lần tải trang, mà `setMode` bên trong lại ghi đè đúng cái đang đè.
  const forced = envForcedMode();
  if (forced) return Promise.resolve(forced);
  if (inFlight) return inFlight;
  inFlight = fetch(`${BOIDE_API_URL}/v1/ui-config`)
    .then((res) => (res.ok ? res.json() : null))
    .then((json) => {
      // Khuôn response chung của BE: { data: {...}, status: "success" }.
      const mode = normalizeMode(json?.data?.ui_mode);
      if (mode) {
        setMode(mode);
        writeCache(mode);
      }
      return currentMode;
    })
    .catch(() => currentMode)
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}
